// Import Dependencies
import React, { Component } from 'react';
import { observer, inject } from 'mobx-react';
import { withRouter, Link, Redirect } from 'react-router-dom';

// Import Images
import local from '~/common/images/map-pin-icon.svg';

// Import Components
import Centering from '~/common/components/Centering';
import BackButton from '~/common/components/BackButton';
import NumberSelection from '~/common/components/NumberSelection';
import LoadingSpinner from '~/common/components/LoadingSpinner';
import NewBatch from '~/common/components/NewBatch';
import SelectedNumberButton from '~/common/components/SelectedNumberButton';
import SelectNumberButton from '~/common/components/SelectNumberButton';
import Stepper from '~/common/components/Stepper';
import HeaderGroup from '~/common/components/HeaderGroup';

import { scBeacon, createPersonalizedPurchaseEvent, gtm, clickCtaEvent, viewContentEvent } from '~/common/utils/tracking.js';
import {
    FLOW_STEP_NUMBERS,
    PATH_LOCAL_SEARCH, PATH_PLANS, PATH_TRIAL_SIGNUP
} from '~/common/utils/consts.js';

// Import Stylesheet
import styles from './local-recommend.css';

@withRouter
@inject('lockNumberStore', 'modalStore', 'localNumbersStore', 'userLocationStore', 'progressStore', 'trialSignupStore')
@observer
export default class LocalRecommend extends Component {
    componentDidMount() {
        const { progressStore, trialSignupStore } = this.props;

        window.scroll({ top: 0, left: 0, behavior: 'smooth' });

        // checks if this is the app entry point and sets corresponding flag
        if (!progressStore.firstView) progressStore.setFirstView(FLOW_STEP_NUMBERS);

        const { isTrialFlow } = trialSignupStore;
        gtm(viewContentEvent(isTrialFlow ? 'trial flow' : 'buy flow', 'numbers', 'local number'));


        this.getLocalNumbersByNpa();
    }

    selectedNumber() {
        const { localNumbersStore } = this.props;
        const selectedNumber = localNumbersStore.selectedNumber;

        if (selectedNumber && selectedNumber.UI) {
            return localNumbersStore.selectedNumber;
        }
        if (localNumbersStore.localNumbers.length > 0) {
            localNumbersStore.setPreSelectedNumberIndex(0);
            return localNumbersStore.selectedNumber;
        }
        return {
            UI: 'Loading...'
        };
    }

    selectNumber(i) {
        const { lockNumberStore, localNumbersStore, modalStore, trialSignupStore } = this.props;

        // change the index if one is passed
        if (i) {
            localNumbersStore.setPreSelectedNumberIndex(i);
        }
        // set selected number in store
        lockNumberStore.setSelectedNumber(localNumbersStore.selectedNumber);
        // set selected number type to local
        lockNumberStore.setSelectedNumberType('Local');

        if (trialSignupStore.isTrialFlow && lockNumberStore.isSelectedAreaCodePremium) {
            modalStore.setModalOptions({
                childComponentString: 'upgradeModal',
                isModalClosable: true,
                modalType: 'xlarge',
                isModalOpen: true
            });
        } else {
            lockNumberStore.lockNumberToSession();
            // track SCBeacon: selected Local number recommended to user
            scBeacon(createPersonalizedPurchaseEvent('1'));
        }
    }

    getLocalNumbersByNpa() {
        const { userLocationStore, localNumbersStore } = this.props;
        const areaCode = localNumbersStore.selectedAreaCode || userLocationStore.localAreaCode;
        const regex = new RegExp('^[0-9]{3}$');
        // redirect to local search if no area code
        if (typeof areaCode !== 'string' || !regex.test(parseInt(areaCode, 10))) this.props.history.push(PATH_LOCAL_SEARCH);

        // fetch a new batch of local numbers
        localNumbersStore.setInitialSearch(true);
        localNumbersStore.getLocalNumbersByNpa(areaCode);
    }

    render() {
        const { localNumbersStore, lockNumberStore, userLocationStore, progressStore, trialSignupStore } = this.props;

        const { localNumbers, localNumbersFetching, localNumberError, initialSearch } = localNumbersStore;
        const { numberIsLocked, lockingNumber, selectedNumber } = lockNumberStore;
        const { userLocation } = userLocationStore;

        // if number is successfully locked
        if (numberIsLocked && lockingNumber) {
            // redirect to the trial page if in trial mode
            const redirectView = trialSignupStore.isTrialFlow ? PATH_TRIAL_SIGNUP : progressStore.nextStepFromNumbers;
            // redirect to billing page or plans page if plan is not selected
            return (
                <Redirect push={true} to={redirectView} />
            );
        }

        // send user to search if there are no numbers in their area or we don't know their area
        if (localNumberError || (!localNumbersFetching && localNumbers.length === 0 && initialSearch)) {
            return <Redirect push={true} to={PATH_LOCAL_SEARCH} />;
        }

        return (
            <div className={styles.wrapper}>
                <Centering lessBottomMargin relative={true}>
                    <Stepper currentStep={FLOW_STEP_NUMBERS} />
                    <BackButton currentStep={FLOW_STEP_NUMBERS} />
                    <HeaderGroup>
                        <h1>
                            Get a great local number.
                        </h1>
                    </HeaderGroup>
                    <img
                        alt="local badge"
                        className={styles.icon}
                        src={local}
                    />
                    <div>
                        <p className={styles.recommend}>
                            We recommend...
                        </p>
                        <SelectedNumberButton number={this.selectedNumber()} onClick={() => this.selectNumber()} />
                    </div>
                    {trialSignupStore.isTrialFlow &&
                        localNumbersStore.isSelectedAreaCodePremium &&
                        <div className={styles['premium-wrapper']}>
                            <p className={styles.premium}>Area code {localNumbersStore.selectedAreaCode} is designated as a premium area code, and is not available for use in the trial version of Grasshopper.</p>
                            <p className={styles.premium}>
                                Still want a number with a premium area code? No problem!<br />
                                Just
                                <Link
                                    onClick={() => {
                                        trialSignupStore.setIsTrialFlow(false);
                                        gtm(clickCtaEvent('purchase the full version of Grasshopper', PATH_PLANS, 'local number recommend'));
                                    }}
                                    to={PATH_PLANS}
                                > purchase the full version of Grasshopper. &#187;</Link>
                            </p>
                        </div>
                    }
                    <div className={styles['new-area-code']}>
                        <Link
                            id="local-number__change-area-code"
                            to={PATH_LOCAL_SEARCH}
                        >
                            Actually, I’d like a different area code &#187;
                        </Link>
                    </div>
                </Centering>
                <NumberSelection>
                    {localNumbersFetching ?
                        <div className={styles['loading-box']}>
                            <p className={styles['great-numbers']}>
                                Or select from these great numbers:
                            </p>
                            <div className={styles.spinner}>
                                <LoadingSpinner />
                            </div>
                            {userLocation ?
                                <p className={styles['great-numbers']}>Fetching...</p>
                                :
                                null
                            }
                        </div>
                        :
                        <div>
                            <p className={styles['great-numbers']}>
                                Or select from these great numbers:
                            </p>
                            <div className={styles['number-selection']}>
                                {localNumbers.map((number, i) => {
                                    if (selectedNumber
                                        && selectedNumber.E164
                                        && localNumbers[i].E164 === selectedNumber.E164
                                        && numberIsLocked) {
                                        return (
                                            <SelectNumberButton
                                                key={number.E164}
                                                number={number}
                                                onClick={() => this.selectNumber(i)}
                                                selected={true}
                                                value={i}
                                            >
                                                {number.UI}
                                            </SelectNumberButton>
                                        );
                                    }
                                    return (
                                        <SelectNumberButton
                                            key={number.E164}
                                            number={number}
                                            onClick={() => this.selectNumber(i)}
                                            value={i}
                                        >
                                            {number.UI}
                                        </SelectNumberButton>
                                    );
                                })}
                            </div>
                            <div className={styles['get-new-batch']}>
                                <NewBatch fetching={localNumbersFetching} onClick={() => this.getLocalNumbersByNpa()} />
                            </div>
                        </div>
                    }
                </NumberSelection>
            </div>
        );
    }
}
